/**
 * useUserPreferences - React hook for managing user preferences
 * Handles loading and updating user page size preferences
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getPageSize, savePageSize } from '../services/preferenceService';
import { useAuth } from './useAuth';

const DEFAULT_PAGE_SIZE = 10;

/**
 * Hook for managing user preferences
 * @returns {Object} Object containing:
 *   - pageSize: Current page size preference
 *   - updatePageSize: Function to update page size
 *   - loading: Whether preferences are being loaded
 *   - error: Any error that occurred during loading
 *   - clearError: Function to clear error state
 */
export const useUserPreferences = () => {
  const { user } = useAuth();
  const userId = user?.sub; // Get userId from user.sub (Cognito subject ID)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  /**
   * Load user preferences from API
   */
  const loadPageSize = useCallback(async () => {
    if (!userId) {
      if (isMountedRef.current) {
        setLoading(false);
      }
      return;
    }

    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }
      
      const prefs = await getPageSize(userId);
      
      if (isMountedRef.current) {
        setPageSize(prefs.pageSize || DEFAULT_PAGE_SIZE);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
      if (isMountedRef.current) {
        setError(err.message);
        // Fall back to default on error
        setPageSize(DEFAULT_PAGE_SIZE);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId]);

  /**
   * Load preferences when userId changes
   */
  useEffect(() => {
    loadPageSize();
  }, [userId, loadPageSize]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Update page size preference
   * @param {number} newPageSize - The new page size to save
   * @throws {Error} If save fails
   */
  const updatePageSize = useCallback(
    async (newPageSize) => {
      if (!userId) {
        const msg = 'Cannot update preferences without userId';
        console.warn(msg);
        throw new Error(msg);
      }

      // Optimistically update UI
      const previousPageSize = pageSize;
      if (isMountedRef.current) {
        setPageSize(newPageSize);
      }

      try {
        await savePageSize(userId, newPageSize);
        // Clear any previous errors on success
        if (isMountedRef.current) {
          setError(null);
        }
      } catch (err) {
        console.error('Failed to save preference:', err);
        if (isMountedRef.current) {
          setError(err.message);
          // Revert to previous value on error
          setPageSize(previousPageSize);
        }
        throw err;
      }
    },
    [userId, pageSize]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    if (isMountedRef.current) {
      setError(null);
    }
  }, []);

  return {
    pageSize,
    updatePageSize,
    loading,
    error,
    clearError
  };
};

export default useUserPreferences;
